import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, CalendarDays, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getCustomerWeeklyShifts, getCustomerEarliestShift } from '../../../../../services/staffingService';

// ─── date helpers ─────────────────────────────────────────────────────────────
const DAY_NAMES   = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  d.setHours(0, 0, 0, 0);
  return d;
}
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function toISO(d)       { return new Date(d).toISOString().split('T')[0]; }
function fmt(d, opts)   { return new Date(d).toLocaleDateString('en-US', opts); }
function fmtTime(d)     { return new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }); }

function getFirstDayOfMonth(year, month) {
  const d = new Date(year, month, 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

// Convert JS getDay() (Sun=0) → Mon-first index (Mon=0 … Sun=6)
function monFirstIndex(jsDay) { return jsDay === 0 ? 6 : jsDay - 1; }

// ─── Shift pill (shared by week + month) ─────────────────────────────────────
function ShiftPill({ shift, compact = false, onClick }) {
  const filled   = shift.filledCount    ?? 0;
  const required = shift.staffsRequired ?? 0;
  const cfg = filled >= required && required > 0
    ? { bg: 'bg-emerald-50', border: '#10b981', badge: 'bg-emerald-100 text-emerald-700' }
    : filled > 0
      ? { bg: 'bg-amber-50',   border: '#f59e0b', badge: 'bg-amber-100   text-amber-700'   }
      : { bg: 'bg-red-50',     border: '#ef4444', badge: 'bg-red-100     text-red-700'     };

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      onClick={onClick}
      className={`cursor-pointer rounded-md ${compact ? 'p-1' : 'p-1.5'} ${cfg.bg} transition-all hover:-translate-y-0.5 hover:shadow-sm`}
      style={{ borderLeft: `3px solid ${cfg.border}` }}
    >
      <p className={`font-semibold text-card-foreground ${compact ? 'text-[10px]' : 'text-[11px]'}`}>
        {fmtTime(shift.startTime)}{!compact && ` – ${fmtTime(shift.endTime)}`}
      </p>
      {!compact && (
        <>
          <p className="truncate text-[11px] text-muted-foreground">{shift.jobLocation}</p>
          {shift.serviceType && (
            <p className="truncate text-[10px] text-muted-foreground/60">{shift.serviceType}</p>
          )}
        </>
      )}
      {required > 0 && (
        <span className={`mt-0.5 inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[9px] font-bold ${cfg.badge}`}>
          {filled}/{required}
        </span>
      )}
    </motion.div>
  );
}

// ─── Week view ────────────────────────────────────────────────────────────────
function WeekView({ shifts, monday, loading, onShiftClick }) {
  const today     = new Date(); today.setHours(0, 0, 0, 0);
  const dayShifts = Array.from({ length: 7 }, () => []);

  shifts.forEach((s) => {
    const diff = Math.round((new Date(s.startTime) - monday) / 86400000);
    if (diff >= 0 && diff < 7) dayShifts[diff].push(s);
  });

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-border bg-muted/50">
        {DAY_NAMES.map((name, i) => {
          const day       = addDays(monday, i);
          const isToday   = day.toDateString() === today.toDateString();
          const openCount = dayShifts[i].filter(s => !s.filledCount || s.filledCount < s.staffsRequired).length;
          return (
            <div key={i} className={`border-r border-border px-2 py-2.5 text-center last:border-r-0 ${isToday ? 'bg-[#CC457D]/5' : ''}`}>
              <p className={`text-xs font-semibold ${isToday ? 'text-[#CC457D]' : 'text-muted-foreground'}`}>
                {name} {fmt(day, { day: 'numeric' })}
              </p>
              <div className="mt-1 flex items-center justify-center gap-1">
                <span className="text-[11px] text-muted-foreground">{dayShifts[i].length} shifts</span>
                {openCount > 0 && (
                  <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{openCount}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Shift slots */}
      {loading ? (
        <div className="grid grid-cols-7 divide-x divide-border">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="min-h-[200px] space-y-2 p-2">
              {[0, 1].map(j => <div key={j} className="h-16 animate-pulse rounded-lg bg-muted" />)}
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-7 divide-x divide-border">
          {dayShifts.map((slots, i) => (
            <div key={i} className="min-h-[180px] space-y-1.5 p-1.5">
              <AnimatePresence>
                {slots.map(s => (
                  <ShiftPill key={s._id} shift={s} onClick={() => onShiftClick(s)} />
                ))}
              </AnimatePresence>
              {slots.length === 0 && <p className="mt-6 text-center text-[11px] text-muted-foreground/50">—</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Month view ───────────────────────────────────────────────────────────────
function MonthView({ shifts, year, month, loading, onShiftClick }) {
  const today        = new Date(); today.setHours(0, 0, 0, 0);
  const firstDay     = getFirstDayOfMonth(year, month);
  const daysInMonth  = getDaysInMonth(year, month);
  const startPadding = monFirstIndex(firstDay.getDay()); // blank cells at start

  // Map ISO date string → shifts[]
  const byDate = {};
  shifts.forEach(s => {
    const iso = toISO(s.startTime);
    if (!byDate[iso]) byDate[iso] = [];
    byDate[iso].push(s);
  });

  // Build grid cells: padding + actual days
  const cells = [
    ...Array.from({ length: startPadding }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1)),
  ];
  // Pad to full weeks
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      {/* Day-of-week header */}
      <div className="grid grid-cols-7 border-b border-border bg-muted/50">
        {DAY_NAMES.map(name => (
          <div key={name} className="border-r border-border px-2 py-2 text-center last:border-r-0">
            <p className="text-xs font-semibold text-muted-foreground">{name}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-7 divide-x divide-border">
          {Array.from({ length: 35 }).map((_, i) => (
            <div key={i} className="min-h-[90px] animate-pulse bg-muted/20" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-7">
          {cells.map((day, i) => {
            if (!day) return <div key={i} className="min-h-[90px] border-b border-r border-border bg-muted/10 last-of-type:border-r-0" />;
            const iso        = toISO(day);
            const daySlots   = byDate[iso] || [];
            const isToday    = day.toDateString() === today.toDateString();
            const isPast     = day < today;
            const colIndex   = i % 7;
            return (
              <div
                key={i}
                className={`min-h-[90px] border-b border-r border-border p-1.5 ${colIndex === 6 ? 'border-r-0' : ''} ${isToday ? 'bg-[#CC457D]/5' : isPast ? 'bg-muted/10' : 'bg-card'}`}
              >
                <p className={`mb-1 text-right text-xs font-semibold ${isToday ? 'flex h-5 w-5 items-center justify-center rounded-full bg-[#CC457D] text-white ml-auto' : isPast ? 'text-muted-foreground/50' : 'text-foreground'}`}>
                  {day.getDate()}
                </p>
                <div className="space-y-0.5">
                  <AnimatePresence>
                    {daySlots.slice(0, 3).map(s => (
                      <ShiftPill key={s._id} shift={s} compact onClick={() => onShiftClick(s)} />
                    ))}
                  </AnimatePresence>
                  {daySlots.length > 3 && (
                    <p className="text-[10px] text-muted-foreground pl-1">+{daySlots.length - 3} more</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── CalendarView ─────────────────────────────────────────────────────────────
/**
 * CalendarView — week and month views for the Work Orders page.
 *
 * Props:
 *   onShiftClick(shift) — navigate to /jobs/:jobId/shift/:shiftId
 */
const CalendarView = ({ onShiftClick }) => {
  const { customerCompanyId } = useParams();

  const [calMode, setCalMode]   = useState('month'); // 'week' | 'month'
  const [monday,  setMonday]    = useState(() => getMonday(new Date()));
  const [monthRef, setMonthRef] = useState(() => {
    const n = new Date(); return { year: n.getFullYear(), month: n.getMonth() };
  });
  const [shifts,  setShifts]    = useState([]);
  const [loading, setLoading]   = useState(true);

  // Jump to earliest upcoming shift on mount
  useEffect(() => {
    getCustomerEarliestShift(customerCompanyId)
      .then(r => {
        const date = r.data.earliestShiftDate;
        if (date) {
          const d = new Date(date);
          setMonday(getMonday(d));
          setMonthRef({ year: d.getFullYear(), month: d.getMonth() });
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerCompanyId]);

  // Determine fetch window based on mode
  const { fetchStart, fetchEnd } = calMode === 'week'
    ? { fetchStart: toISO(monday), fetchEnd: toISO(addDays(monday, 6)) }
    : (() => {
        const first = getFirstDayOfMonth(monthRef.year, monthRef.month);
        const last  = new Date(monthRef.year, monthRef.month + 1, 0);
        return { fetchStart: toISO(first), fetchEnd: toISO(last) };
      })();

  const fetchShifts = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getCustomerWeeklyShifts(customerCompanyId, fetchStart, fetchEnd);
      setShifts(resp.data.shifts || []);
    } catch {
      setShifts([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerCompanyId, fetchStart, fetchEnd]);

  useEffect(() => { fetchShifts(); }, [fetchShifts]);

  // Navigation helpers
  const goPrev = () => {
    if (calMode === 'week') setMonday(d => addDays(d, -7));
    else setMonthRef(({ year, month }) => month === 0 ? { year: year - 1, month: 11 } : { year, month: month - 1 });
  };
  const goNext = () => {
    if (calMode === 'week') setMonday(d => addDays(d, 7));
    else setMonthRef(({ year, month }) => month === 11 ? { year: year + 1, month: 0 } : { year, month: month + 1 });
  };
  const goToday = () => {
    const n = new Date();
    setMonday(getMonday(n));
    setMonthRef({ year: n.getFullYear(), month: n.getMonth() });
  };

  // Label
  const sunday    = addDays(monday, 6);
  const weekLabel = `${fmt(monday, { month: 'short', day: 'numeric' })} – ${fmt(sunday, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  const monthLabel = `${MONTH_NAMES[monthRef.month]} ${monthRef.year}`;

  return (
    <div>
      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        {/* Nav: prev / today / next + label */}
        <div className="flex items-center gap-2">
          <button onClick={goPrev} className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-muted transition-colors">
            <ChevronLeft size={16} />
          </button>
          <button onClick={goToday} className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors">
            Today
          </button>
          <button onClick={goNext} className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-muted transition-colors">
            <ChevronRight size={16} />
          </button>
          <span className="ml-2 text-sm font-medium text-foreground">
            {calMode === 'week' ? weekLabel : monthLabel}
          </span>
        </div>

        {/* Week / Month toggle — underline-tab style (distinct from filled toggle in parent) */}
        <div className="flex items-center gap-1 border-b border-transparent">
          {[{key:'week',label:'Week',Icon:CalendarDays},{key:'month',label:'Month',Icon:Calendar}].map(({key,label,Icon}) => (
            <button
              key={key}
              onClick={() => setCalMode(key)}
              className={`relative flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                calMode === key
                  ? 'text-[#404880]'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon size={13} /> {label}
              {calMode === key && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t bg-[#404880]" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Calendar grid ───────────────────────────────────────────────── */}
      {calMode === 'week' ? (
        <WeekView
          shifts={shifts}
          monday={monday}
          loading={loading}
          onShiftClick={onShiftClick}
        />
      ) : (
        <MonthView
          shifts={shifts}
          year={monthRef.year}
          month={monthRef.month}
          loading={loading}
          onShiftClick={onShiftClick}
        />
      )}

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        {[
          { color: '#ef4444', label: 'Unfilled' },
          { color: '#f59e0b', label: 'Partial'  },
          { color: '#10b981', label: 'Filled'   },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className="h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CalendarView;
